// Agent scheduled-tasks panel (extracted from agent-kb-manager.js, Stage B4).
//
// Self-contained: keeps its own state and receives the active agent + settings via
// loadTasks(agentName, settings) — it never mutates the parent module's state.
import { getHeaders } from './api.js';
import { escapeHtml } from './html-escape.js';
import { showToast } from './toast.js';

let currentAgent = null;
let currentSettings = {};

// ── Scheduled Tasks ────────────────────────────────────────────────────

let tasksList = [];
let editingTaskId = null;

function formatInterval(seconds) {
    if (seconds >= 604800) return `${Math.round(seconds / 604800)}d`;
    if (seconds >= 86400) return `${Math.round(seconds / 86400)}d`;
    if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`;
    if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
    return `${seconds}s`;
}

function formatRelativeTime(isoStr) {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now - d;
    const absDiff = Math.abs(diffMs);
    const future = diffMs < 0;
    if (absDiff < 60000) return future ? 'in <1m' : '<1m ago';
    if (absDiff < 3600000) {
        const m = Math.round(absDiff / 60000);
        return future ? `in ${m}m` : `${m}m ago`;
    }
    if (absDiff < 86400000) {
        const h = Math.round(absDiff / 3600000);
        return future ? `in ${h}h` : `${h}h ago`;
    }
    const days = Math.round(absDiff / 86400000);
    return future ? `in ${days}d` : `${days}d ago`;
}

export async function loadTasks(agentName, settings) {
    currentAgent = agentName;
    currentSettings = settings;
    if (!currentAgent) return;
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks`, { headers });
        if (!resp.ok) throw new Error('Failed to load tasks');
        const data = await resp.json();
        tasksList = data.tasks || [];
        renderTasksList();
    } catch (error) {
        console.error('Failed to load tasks:', error);
        const list = document.getElementById('agent-tasks-list');
        if (list) list.innerHTML = '<p class="tasks-empty">Failed to load tasks</p>';
    }
}

function renderTaskChips(task) {
    const disabledSet = new Set(task.disabled_tools || []);
    const disabledChains = new Set(task.disabled_chains || []);

    // Collect agent's tools
    const allTools = [...(currentSettings?.tools || [])];
    for (const [flag, toolName] of Object.entries(TASK_SETTINGS_TOOL_FLAGS)) {
        if (currentSettings?.[flag] && !allTools.includes(toolName)) {
            allTools.push(toolName);
        }
    }

    let chips = '';
    for (const toolName of allTools) {
        if (disabledSet.has(toolName)) continue;
        const meta = TASK_TOOL_DISPLAY[toolName];
        if (!meta) continue;
        chips += `<span class="tool-chip tool-chip-small" style="--tool-color:${meta.color}" title="${toolName}">${meta.icon}<span>${meta.label}</span></span>`;
    }

    // Chain chips
    const chains = currentSettings?.tool_chains || [];
    for (const chain of chains) {
        if (!chain.enabled || disabledChains.has(chain.id)) continue;
        const chainToolName = `chain_${chain.id.replace(/-/g, '_')}`;
        if (disabledSet.has(chainToolName)) continue;
        chips += `<span class="tool-chip tool-chip-small tool-chip-chain" style="--tool-color:#f59e0b" title="Chain: ${escapeHtml(chain.name || chain.id)}"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg><span>${escapeHtml(chain.name || chain.id)}</span></span>`;
    }

    return chips;
}

function renderTasksList() {
    const list = document.getElementById('agent-tasks-list');
    if (!list) return;

    // Hide editor/runs panels
    document.getElementById('task-editor')?.classList.add('hidden');
    document.getElementById('task-runs-panel')?.classList.add('hidden');
    document.querySelector('.tasks-toolbar')?.classList.remove('hidden');

    if (tasksList.length === 0) {
        list.innerHTML = '<p class="tasks-empty">No scheduled tasks yet</p>';
        return;
    }

    list.innerHTML = tasksList.map(task => {
        const isActive = task.enabled && (task.schedule_type === 'recurring' || task.run_count === 0);
        const isDone = task.schedule_type === 'once' && task.run_count > 0;
        const statusClass = !task.enabled ? 'paused' : isDone ? 'done' : 'active';
        const statusLabel = !task.enabled ? 'Paused' : isDone ? 'Completed' : 'Active';

        let scheduleText;
        if (task.schedule_type === 'recurring') {
            scheduleText = `Every ${formatInterval(task.interval_seconds)}`;
        } else {
            scheduleText = task.run_at
                ? `Once: ${new Date(task.run_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : 'Once';
        }

        return `
            <div class="task-card" data-task-id="${task.id}">
                <div class="task-card-header">
                    <div class="task-card-status ${statusClass}"></div>
                    <div class="task-card-info">
                        <span class="task-card-name">${escapeHtml(task.name)}</span>
                        <span class="task-card-schedule">${scheduleText}</span>
                    </div>
                    <span class="task-status-badge ${statusClass}">${statusLabel}</span>
                </div>
                <div class="task-card-prompt">${escapeHtml(task.prompt.length > 100 ? task.prompt.slice(0, 100) + '...' : task.prompt)}</div>
                <div class="task-card-chips">${renderTaskChips(task)}</div>
                <div class="task-card-meta">
                    ${task.last_run_at ? `<span>Last: ${formatRelativeTime(task.last_run_at)}</span>` : ''}
                    ${task.next_run_at && isActive ? `<span>Next: ${formatRelativeTime(task.next_run_at)}</span>` : ''}
                    ${task.run_count > 0 ? `<span>${task.run_count} run${task.run_count !== 1 ? 's' : ''}</span>` : ''}
                </div>
                <div class="task-card-actions">
                    <button class="task-action-btn" data-action="edit" title="Edit">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button class="task-action-btn ${task.enabled ? 'task-btn-active' : 'task-btn-paused'}" data-action="toggle" title="${task.enabled ? 'Pause' : 'Enable'}">
                        ${task.enabled
                            ? '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>'
                            : '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>'
                        }
                    </button>
                    <button class="task-action-btn" data-action="run" title="Run Now">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                    </button>
                    <button class="task-action-btn" data-action="runs" title="View Runs">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                    </button>
                    <button class="task-action-btn danger" data-action="delete" title="Delete">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                </div>
            </div>
        `;
    }).join('');

    // Wire action buttons
    list.querySelectorAll('.task-action-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const card = btn.closest('.task-card');
            const taskId = parseInt(card.dataset.taskId);
            const action = btn.dataset.action;
            if (action === 'edit') showTaskEditor(taskId);
            else if (action === 'run') runTaskNow(taskId);
            else if (action === 'toggle') toggleTaskEnabled(taskId);
            else if (action === 'runs') showTaskRuns(taskId);
            else if (action === 'delete') deleteTask(taskId);
        });
    });
}

// Tool display metadata (mirrors main.js TOOL_DISPLAY)
const TASK_TOOL_DISPLAY = {
    rag_search:     { label: 'RAG Search',   color: '#3b82f6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><circle cx="12" cy="10" r="3"/><path d="m15 13-1.5-1.5"/></svg>' },
    python_exec:    { label: 'Python',       color: '#22c55e', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>' },
    shell_exec:     { label: 'Shell',        color: '#f97316', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>' },
    file_ops:       { label: 'Files',        color: '#eab308', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"/></svg>' },
    browser_check: { label: 'Browser', color: '#0ea5e9', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>' },
    web_fetch:      { label: 'Web Fetch',    color: '#06b6d4', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>' },
    http_request:   { label: 'HTTP',         color: '#10b981', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>' },
    email_send:     { label: 'Email',        color: '#f97316', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' },
    image_generate: { label: 'Image Gen',    color: '#e879f9', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' },
    scholar:        { label: 'Scholar',      color: '#6366f1', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>' },
    agent_db:       { label: 'Database',     color: '#a855f7', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/></svg>' },
    doc_create:     { label: 'Doc Create',   color: '#ec4899', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M12 18v-6"/><path d="M9 15h6"/></svg>' },
    calculator:     { label: 'Calc',         color: '#6b7280', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/></svg>' },
    datetime:       { label: 'DateTime',     color: '#6b7280', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' },
    web_search:     { label: 'Smart Search', color: '#f59e0b', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.34-4.34"/></svg>' },
    vision:         { label: 'Vision',       color: '#8b5cf6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>' },
    text_cleanup:   { label: 'Proofread',    color: '#14b8a6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>' },
    recall:         { label: 'Recall',        color: '#a78bfa', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 10 9 10"/><path d="M12 7v5l3 2"/></svg>' },
    synthesize:    { label: 'Synthesize',    color: '#a78bfa', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h6"/><path d="M4 12h6"/><path d="M4 19h6"/><path d="M10 5c5 0 4 7 9 7"/><path d="M10 19c5 0 4-7 9-7"/></svg>' },
    brain_query:   { label: 'Brains',        color: '#f472b6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/></svg>' },
};

const TASK_SETTINGS_TOOL_FLAGS = {
    tool_vision: 'vision',
    tool_text_cleanup: 'text_cleanup',
    tool_doc_create: 'doc_create',
};

// Track per-task disabled tools/chains/planning (independent from plus menu state)
let taskDisabledTools = new Set();
let taskPlanningEnabled = true;

export async function showTaskEditor(taskId) {
    editingTaskId = taskId;
    const editor = document.getElementById('task-editor');
    const title = document.getElementById('task-editor-title');
    if (!editor) return;

    // Hide list toolbar, show editor
    document.querySelector('.tasks-toolbar')?.classList.add('hidden');
    document.getElementById('agent-tasks-list').innerHTML = '';
    document.getElementById('task-runs-panel')?.classList.add('hidden');
    editor.classList.remove('hidden');
    title.textContent = taskId ? 'Edit Task' : 'New Task';

    // Reset per-task state
    taskDisabledTools = new Set();
    taskPlanningEnabled = true;

    // ── Reset model selector ──
    const modelInput = document.getElementById('task-model');
    const modelDisplay = document.getElementById('task-model-display');
    if (modelInput) modelInput.value = '';
    if (modelDisplay) { modelDisplay.textContent = ''; modelDisplay.title = ''; }

    // ── Build tool/chain/planning chips ──
    const chipsContainer = document.getElementById('task-chips-container');
    if (chipsContainer) {
        chipsContainer.innerHTML = '';

        // Collect all tools (same logic as populateToolToggles)
        const allTools = [...(currentSettings?.tools || [])];
        for (const [flag, toolName] of Object.entries(TASK_SETTINGS_TOOL_FLAGS)) {
            if (currentSettings?.[flag] && !allTools.includes(toolName)) {
                allTools.push(toolName);
            }
        }
        const toolConfigs = currentSettings?.tool_configs || {};

        // Tool chips
        for (const toolName of allTools) {
            const meta = TASK_TOOL_DISPLAY[toolName];
            if (!meta) continue;
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'tool-chip';
            chip.dataset.tool = toolName;
            chip.style.setProperty('--tool-color', meta.color);
            chip.innerHTML = `${meta.icon}<span>${meta.label}</span>`;
            chip.title = toolName;

            const defaultOn = toolConfigs[toolName]?.default_on !== false;
            if (!defaultOn) {
                taskDisabledTools.add(toolName);
                chip.classList.add('disabled');
            }

            chip.addEventListener('click', () => {
                if (taskDisabledTools.has(toolName)) {
                    taskDisabledTools.delete(toolName);
                    chip.classList.remove('disabled');
                } else {
                    taskDisabledTools.add(toolName);
                    chip.classList.add('disabled');
                }
            });
            chipsContainer.appendChild(chip);
        }

        // Chain chips
        const chains = currentSettings?.tool_chains || [];
        const chainIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
        for (const chain of chains) {
            if (!chain.enabled) continue;
            const chainToolName = `chain_${chain.id.replace(/-/g, '_')}`;
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'tool-chip tool-chip-chain';
            chip.dataset.tool = chainToolName;
            chip.dataset.chainId = chain.id;
            chip.style.setProperty('--tool-color', '#f59e0b');
            chip.innerHTML = `${chainIcon}<span>${escapeHtml(chain.name || chain.id)}</span>`;
            chip.title = `Chain: ${chain.description || chain.name || chain.id}`;

            const defaultOn = chain.default_on !== false;
            if (!defaultOn) {
                taskDisabledTools.add(chainToolName);
                chip.classList.add('disabled');
            }

            chip.addEventListener('click', () => {
                if (taskDisabledTools.has(chainToolName)) {
                    taskDisabledTools.delete(chainToolName);
                    chip.classList.remove('disabled');
                } else {
                    taskDisabledTools.add(chainToolName);
                    chip.classList.add('disabled');
                }
            });
            chipsContainer.appendChild(chip);
        }

        // Planning chip
        if (currentSettings?.use_planning) {
            const defaultOn = currentSettings.planning_default_on !== false;
            taskPlanningEnabled = defaultOn;
            const planIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>';
            const planChip = document.createElement('button');
            planChip.type = 'button';
            planChip.className = 'tool-chip tool-chip-planning';
            planChip.dataset.tool = '__planning__';
            planChip.style.setProperty('--tool-color', '#a78bfa');
            planChip.innerHTML = `${planIcon}<span>Planning</span>`;
            planChip.title = 'Toggle planning step';

            if (!defaultOn) planChip.classList.add('disabled');

            planChip.addEventListener('click', () => {
                taskPlanningEnabled = !taskPlanningEnabled;
                planChip.classList.toggle('disabled', !taskPlanningEnabled);
            });
            chipsContainer.appendChild(planChip);
        }

        // Show placeholder if no tools
        if (chipsContainer.children.length === 0) {
            chipsContainer.innerHTML = '<span style="color:#666;font-size:0.8rem">No tools configured for this agent</span>';
        }
    }

    // ── Reset form fields ──
    document.getElementById('task-name').value = '';
    document.getElementById('task-prompt').value = '';
    if (modelInput) modelInput.value = '';
    document.querySelector('input[name="task-schedule"][value="once"]').checked = true;
    document.getElementById('task-run-at-row')?.classList.remove('hidden');
    document.getElementById('task-interval-row')?.classList.add('hidden');
    document.getElementById('task-run-at').value = '';
    document.querySelectorAll('.task-interval-btn').forEach(b => b.classList.remove('active'));
    document.getElementById('task-custom-interval').value = '';
    // Reset search level to Off
    document.querySelectorAll('.task-search-btn').forEach(b => {
        b.classList.toggle('active', b.dataset.depth === 'off');
    });
    document.getElementById('task-continue-session').checked = false;
    document.getElementById('task-editor-error').textContent = '';

    // ── If editing, populate from existing task ──
    if (taskId) {
        const task = tasksList.find(t => t.id === taskId);
        if (task) {
            document.getElementById('task-name').value = task.name;
            document.getElementById('task-prompt').value = task.prompt;
            if (task.model && modelInput) {
                modelInput.value = task.model;
                if (modelDisplay) {
                    modelDisplay.textContent = task.model;
                    modelDisplay.title = task.model;
                }
                const clearBtn = document.getElementById('task-model-clear');
                if (clearBtn) clearBtn.textContent = '×';
            }

            if (task.schedule_type === 'recurring') {
                document.querySelector('input[name="task-schedule"][value="recurring"]').checked = true;
                document.getElementById('task-run-at-row')?.classList.add('hidden');
                document.getElementById('task-interval-row')?.classList.remove('hidden');
                const presetBtn = document.querySelector(`.task-interval-btn[data-seconds="${task.interval_seconds}"]`);
                if (presetBtn) {
                    presetBtn.classList.add('active');
                } else if (task.interval_seconds) {
                    document.getElementById('task-custom-interval').value = task.interval_seconds;
                    document.getElementById('task-custom-unit').value = '1';
                }
            } else if (task.run_at) {
                const d = new Date(task.run_at);
                const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                document.getElementById('task-run-at').value = local;
            }

            // Search level
            const depth = task.web_search ? (task.web_search_depth || 'surface') : 'off';
            document.querySelectorAll('.task-search-btn').forEach(b => {
                b.classList.toggle('active', b.dataset.depth === depth);
            });

            // Apply task's disabled tools to chips
            const disabled = task.disabled_tools || [];
            disabled.forEach(t => taskDisabledTools.add(t));
            chipsContainer?.querySelectorAll('.tool-chip').forEach(chip => {
                const name = chip.dataset.tool;
                if (taskDisabledTools.has(name)) chip.classList.add('disabled');
            });

            // Disabled chains
            const disabledChains = task.disabled_chains || [];
            chipsContainer?.querySelectorAll('.tool-chip-chain').forEach(chip => {
                if (disabledChains.includes(chip.dataset.chainId)) {
                    const chainTool = chip.dataset.tool;
                    taskDisabledTools.add(chainTool);
                    chip.classList.add('disabled');
                }
            });

            // Planning
            if (task.disable_planning) {
                taskPlanningEnabled = false;
                const planChip = chipsContainer?.querySelector('[data-tool="__planning__"]');
                if (planChip) planChip.classList.add('disabled');
            }

            document.getElementById('task-continue-session').checked = !!task.continue_session;
        }
    }
}

export function hideTaskEditor() {
    document.getElementById('task-editor')?.classList.add('hidden');
    editingTaskId = null;
    renderTasksList();
}

export async function handleSaveTask() {
    const errorEl = document.getElementById('task-editor-error');
    const name = document.getElementById('task-name').value.trim();
    const prompt = document.getElementById('task-prompt').value.trim();
    const model = document.getElementById('task-model')?.value || '';

    if (!name) { errorEl.textContent = 'Name is required'; return; }
    if (!prompt) { errorEl.textContent = 'Prompt is required'; return; }

    const scheduleType = document.querySelector('input[name="task-schedule"]:checked')?.value || 'once';
    let runAt = null;
    let intervalSeconds = null;

    if (scheduleType === 'once') {
        const runAtVal = document.getElementById('task-run-at').value;
        if (runAtVal) {
            runAt = new Date(runAtVal).toISOString();
        } else {
            errorEl.textContent = 'Run at time is required for one-time tasks';
            return;
        }
    } else {
        const activePreset = document.querySelector('.task-interval-btn.active');
        const customVal = document.getElementById('task-custom-interval').value;
        if (activePreset) {
            intervalSeconds = parseInt(activePreset.dataset.seconds);
        } else if (customVal) {
            const unit = parseInt(document.getElementById('task-custom-unit').value);
            intervalSeconds = parseInt(customVal) * unit;
        }
        if (!intervalSeconds || intervalSeconds <= 0) {
            errorEl.textContent = 'Select an interval for recurring tasks';
            return;
        }
    }

    // Gather disabled tools from chips (exclude chains and planning)
    const disabledTools = [];
    const disabledChains = [];
    taskDisabledTools.forEach(name => {
        if (name === '__planning__') return;
        if (name.startsWith('chain_')) {
            // Find the original chain ID from the chip
            const chip = document.querySelector(`.tool-chip-chain[data-tool="${name}"]`);
            if (chip?.dataset.chainId) disabledChains.push(chip.dataset.chainId);
        } else {
            disabledTools.push(name);
        }
    });

    const disablePlanning = !taskPlanningEnabled;
    const searchDepth = document.querySelector('.task-search-btn.active')?.dataset.depth || 'off';
    const webSearch = searchDepth !== 'off';
    const continueSession = document.getElementById('task-continue-session').checked;

    const body = {
        name, prompt, model: model || null,
        schedule_type: scheduleType,
        run_at: runAt, interval_seconds: intervalSeconds,
        disabled_tools: disabledTools.length ? disabledTools : null,
        disabled_chains: disabledChains.length ? disabledChains : null,
        disable_planning: disablePlanning,
        web_search: webSearch,
        web_search_depth: webSearch ? searchDepth : 'surface',
        continue_session: continueSession,
    };

    const headers = getHeaders();
    try {
        let resp;
        if (editingTaskId) {
            resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks/${editingTaskId}`, {
                method: 'PUT', headers, body: JSON.stringify(body)
            });
        } else {
            resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks`, {
                method: 'POST', headers, body: JSON.stringify(body)
            });
        }
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to save task');
        }
        hideTaskEditor();
        await loadTasks();
    } catch (error) {
        errorEl.textContent = error.message;
    }
}

async function runTaskNow(taskId) {
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks/${taskId}/run`, {
            method: 'POST', headers
        });
        if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            throw new Error(err.detail || 'Failed to trigger task');
        }
        showToast('Task triggered', 'success');
        // Refresh card after a short delay to show updated state
        setTimeout(() => loadTasks(), 2000);
    } catch (error) {
        showToast(error.message, 'error');
    }
}

async function toggleTaskEnabled(taskId) {
    const task = tasksList.find(t => t.id === taskId);
    if (!task) return;
    const headers = getHeaders();
    const newEnabled = !task.enabled;
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks/${taskId}`, {
            method: 'PUT', headers,
            body: JSON.stringify({ enabled: newEnabled })
        });
        if (!resp.ok) throw new Error('Failed to toggle task');
        showToast(newEnabled ? 'Task enabled' : 'Task paused', 'success');
        await loadTasks();
    } catch (error) {
        alert(error.message);
    }
}

async function deleteTask(taskId) {
    const task = tasksList.find(t => t.id === taskId);
    if (!task) return;
    if (!confirm(`Delete task "${task.name}"? This will also delete all run history.`)) return;
    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks/${taskId}`, {
            method: 'DELETE', headers
        });
        if (!resp.ok) throw new Error('Failed to delete task');
        await loadTasks();
    } catch (error) {
        alert(error.message);
    }
}

async function showTaskRuns(taskId) {
    const task = tasksList.find(t => t.id === taskId);
    if (!task) return;

    // Hide list and toolbar, show runs panel
    document.querySelector('.tasks-toolbar')?.classList.add('hidden');
    document.getElementById('agent-tasks-list').innerHTML = '';
    document.getElementById('task-editor')?.classList.add('hidden');
    const panel = document.getElementById('task-runs-panel');
    panel?.classList.remove('hidden');

    document.getElementById('task-runs-title').textContent = `Runs: ${task.name}`;
    const list = document.getElementById('task-runs-list');
    list.innerHTML = '<p class="tasks-empty">Loading...</p>';

    const headers = getHeaders();
    try {
        const resp = await fetch(`/agents/${encodeURIComponent(currentAgent)}/tasks/${taskId}/runs`, { headers });
        if (!resp.ok) throw new Error('Failed to load runs');
        const data = await resp.json();
        const runs = data.runs || [];

        if (runs.length === 0) {
            list.innerHTML = '<p class="tasks-empty">No runs yet</p>';
            return;
        }

        list.innerHTML = runs.map(run => {
            const statusIcon = run.status === 'success'
                ? '<span class="run-status success" title="Success">&#10003;</span>'
                : run.status === 'error'
                    ? '<span class="run-status error" title="Error">&#10007;</span>'
                    : '<span class="run-status running" title="Running">&#8987;</span>';

            const duration = run.completed_at && run.started_at
                ? `${((new Date(run.completed_at) - new Date(run.started_at)) / 1000).toFixed(1)}s`
                : '—';

            const toolsUsed = (run.tools_used || []).length;

            return `
                <div class="task-run-item">
                    <div class="task-run-summary" onclick="this.parentElement.classList.toggle('expanded')">
                        ${statusIcon}
                        <span class="run-time">${new Date(run.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        <span class="run-duration">${duration}</span>
                        ${toolsUsed ? `<span class="run-tools">${toolsUsed} tool${toolsUsed !== 1 ? 's' : ''}</span>` : ''}
                        ${run.tokens_used ? `<span class="run-tokens">${run.tokens_used} tok</span>` : ''}
                        <svg class="run-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                    <div class="task-run-detail">
                        ${run.prompt ? `<div class="run-section"><strong>Prompt:</strong><pre>${escapeHtml(run.prompt)}</pre></div>` : ''}
                        ${run.response ? `<div class="run-section"><strong>Response:</strong><pre>${escapeHtml(run.response)}</pre></div>` : ''}
                        ${run.error ? `<div class="run-section run-error"><strong>Error:</strong><pre>${escapeHtml(run.error)}</pre></div>` : ''}
                        ${toolsUsed ? `<div class="run-section"><strong>Tools:</strong> ${escapeHtml(run.tools_used.join(', '))}</div>` : ''}
                    </div>
                </div>
            `;
        }).join('');
    } catch (error) {
        list.innerHTML = `<p class="tasks-empty">Failed to load runs: ${escapeHtml(error.message)}</p>`;
    }
}

export function hideTaskRuns() {
    document.getElementById('task-runs-panel')?.classList.add('hidden');
    renderTasksList();
}
